<template>
  <div class="tenant-select-page">
    <el-card class="tenant-select-card">
      <template #header>
        <div class="card-header">
          <h2>{{ t('routes.tenant-select.title') }}</h2>
          <p class="subtitle">{{ t('routes.tenant-select.subtitle') }}</p>
        </div>
      </template>

      <el-radio-group v-model="selectedTenantId" class="tenant-list" v-if="tenantOptions.length">
        <el-radio
          v-for="tenant in tenantOptions"
          :key="tenant.tenant_id"
          :value="tenant.tenant_id"
          class="tenant-option"
          border
        >
          <div class="tenant-info">
            <span class="tenant-name">{{ tenant.tenant_name }}</span>
            <span class="tenant-roles">{{ tenant.roles.join(' / ') }}</span>
          </div>
        </el-radio>
      </el-radio-group>

      <el-empty v-else-if="isBlockedNoMembership" :description="t('routes.tenant-select.noMembership')">
        <el-button type="primary" @click="handleLogout">{{ t('common.logout') }}</el-button>
      </el-empty>

      <el-empty v-else :description="t('routes.tenant-select.empty')" />

      <div class="actions" v-if="tenantOptions.length">
        <el-button type="primary" :loading="loading" :disabled="!selectedTenantId" @click="handleEnter">
          {{ t('routes.tenant-select.enter') }}
        </el-button>
        <el-button @click="handleLogout">{{ t('common.logout') }}</el-button>
      </div>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import type { TenantInfo } from '@/utils/authStorage'

const { t } = useI18n()

/** 无任何岗位被拦截（query.blocked=no-membership）→ 展示无权限提示，仅可退出登录。 */
const isBlockedNoMembership = computed(() => {
  return router.currentRoute.value.query.blocked === 'no-membership'
})
const router = useRouter()
const authStore = useAuthStore()

const selectedTenantId = ref<string | null>(null)
const loading = ref(false)

/** 可用租户（排除全局角色条目；全局角色不参与租户身份选择）。 */
const tenantOptions = computed<TenantInfo[]>(() =>
  authStore.availableTenants.filter((item) => !item.is_global && item.tenant_id)
)

async function handleEnter() {
  if (!selectedTenantId.value) return
  loading.value = true
  try {
    await authStore.switchCurrentTenant(selectedTenantId.value)
    const redirect = typeof router.currentRoute.value.query.redirect === 'string'
      ? router.currentRoute.value.query.redirect
      : '/dashboard'
    router.push(redirect)
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : t('common.messages.error'))
  } finally {
    loading.value = false
  }
}

async function handleLogout() {
  await authStore.logout()
  router.push('/login')
}
</script>

<style scoped lang="scss">
.tenant-select-page {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--el-bg-color-page);

  .tenant-select-card {
    width: 480px;
    max-width: 90vw;

    .card-header {
      h2 {
        margin: 0 0 8px;
      }

      .subtitle {
        margin: 0;
        color: var(--el-text-color-secondary);
        font-size: 13px;
      }
    }

    .tenant-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
      width: 100%;

      .tenant-option {
        height: auto;
        padding: 12px 16px;
        margin: 0;
        width: 100%;

        .tenant-info {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .tenant-name {
            font-weight: 600;
            font-size: 15px;
          }

          .tenant-roles {
            color: var(--el-text-color-secondary);
            font-size: 12px;
          }
        }
      }
    }

    .actions {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }
  }
}
</style>
